<template>
  <div class="login-page">
    <b-container class="login-shell">
      <b-row class="login-frame no-gutters">
        <b-col lg="5" class="d-none d-lg-flex">
          <section class="brand-panel">
            <p class="eyebrow">Account Recovery</p>
            <h1>Regain access without losing momentum in your workspace.</h1>
            <p class="brand-copy">
              Request a secure password reset link, confirm your identity through email, and return
              to your dashboard with minimal interruption.
            </p>

            <div class="brand-metrics">
              <div class="metric-card">
                <span class="metric-value">Protected</span>
                <span class="metric-label">
                  Reset links are delivered securely to the email tied to your account.
                </span>
              </div>
              <div class="metric-card">
                <span class="metric-value">Guided</span>
                <span class="metric-label">
                  The recovery flow is designed to get you back into your account quickly.
                </span>
              </div>
            </div>
          </section>
        </b-col>

        <b-col cols="12" lg="7">
          <section class="form-panel">
            <div class="form-panel__inner">
              <div class="mobile-brand d-lg-none">
                <p class="eyebrow mb-2">Account Recovery</p>
                <h1>Reset your password</h1>
              </div>

              <div class="form-header">
                <p class="form-kicker">Password reset</p>
                <h2>Recover your account</h2>
                <p class="form-subtitle">
                  Enter the email address linked to your HPPDM account and we will send you a reset
                  link.
                </p>
              </div>

              <div v-if="successMessage" class="status-alert status-alert--success" role="alert">
                {{ successMessage }}
              </div>

              <div v-if="errorMessage" class="status-alert status-alert--danger" role="alert">
                {{ errorMessage }}
              </div>

              <ValidationObserver v-slot="{ passes }">
                <form class="login-form" @submit.prevent="passes(submitResetRequest)">
                  <base-input
                    v-model.trim="form.email"
                    height="3.35rem"
                    placeholder="name@organization.org"
                    width="100%"
                    rules="required|email"
                    name="Email address"
                    label="Email address"
                    description="Use the email currently associated with your account."
                  />

                  <button type="submit" class="login-button" :disabled="loading">
                    <b-spinner v-show="loading" small class="mr-2" />
                    <span>{{ loading ? 'Sending reset link...' : 'Send reset link' }}</span>
                  </button>

                  <div class="auth-switch">
                    <span class="mr-1">Remembered your password?</span>
                    <b-link class="text-decoration-none" to="/auth/login">Back to login</b-link>
                  </div>
                </form>
              </ValidationObserver>
            </div>
          </section>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import { RESET_PASSWORD } from '../../ngo/config/api-modules/api.auth';

export default {
  name: 'ForgotPassword',
  data() {
    return {
      form: {
        email: '',
      },
      loading: false,
      successMessage: '',
      errorMessage: '',
    };
  },
  methods: {
    submitResetRequest() {
      this.loading = true;
      this.successMessage = '';
      this.errorMessage = '';

      RESET_PASSWORD(this.form)
        .then(() => {
          this.successMessage = `${this.form.email}, please check your inbox for the reset link.`;
          this.$toast.show({
            type: 'SUCCESS',
            message: `${this.form.email}, please check your inbox.`,
          });
        })
        .catch(() => {
          this.errorMessage = `We couldn't send a reset link to ${this.form.email}.`;
          this.$toast.show({
            type: 'ERROR',
            message: `Failed sending email to ${this.form.email}`,
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.login-page {
  position: relative;
  min-height: 100vh;
  padding: 2rem 0;
  background: radial-gradient(circle at top left, rgba(48, 171, 76, 0.2), transparent 28%),
    radial-gradient(circle at bottom right, rgba(13, 34, 20, 0.18), transparent 30%),
    linear-gradient(135deg, #f3f8f3 0%, #edf3ee 45%, #e4ece5 100%);
}

.login-page::before {
  content: '';
  position: absolute;
  inset: 0;
  background-image: linear-gradient(rgba(255, 255, 255, 0.26) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.26) 1px, transparent 1px);
  background-size: 120px 120px;
  opacity: 0.45;
  pointer-events: none;
}

.login-shell {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  min-height: calc(100vh - 4rem);
}

.login-frame {
  width: 100%;
  overflow: hidden;
  border: 1px solid rgba(16, 46, 25, 0.08);
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.78);
  box-shadow: 0 30px 90px rgba(20, 43, 27, 0.15);
  backdrop-filter: blur(14px);
}

.brand-panel {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 100%;
  padding: 3.5rem 3rem;
  color: #f8fff8;
  background: linear-gradient(180deg, rgba(5, 20, 10, 0.12), rgba(5, 20, 10, 0.2)),
    linear-gradient(155deg, #0d3418 0%, #195426 52%, #30ab4c 100%);
}

.brand-panel::after {
  content: '';
  position: absolute;
  width: 14rem;
  height: 14rem;
  right: -3rem;
  bottom: -4rem;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  filter: blur(2px);
}

.eyebrow,
.form-kicker {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.eyebrow::before,
.form-kicker::before {
  content: '';
  width: 2.5rem;
  height: 1px;
  background: currentColor;
  opacity: 0.65;
}

.brand-panel h1,
.mobile-brand h1,
.form-header h2 {
  margin: 0;
  font-size: clamp(2rem, 3vw, 3rem);
  line-height: 1.1;
  letter-spacing: -0.04em;
}

.brand-copy,
.form-subtitle {
  margin: 1.15rem 0 0;
  font-size: 1rem;
  line-height: 1.75;
}

.brand-copy {
  max-width: 30rem;
  color: rgba(248, 255, 248, 0.82);
}

.brand-metrics {
  display: grid;
  gap: 1rem;
  margin-top: 2rem;
}

.metric-card {
  padding: 1.2rem 1.25rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(8px);
}

.metric-value {
  display: block;
  margin-bottom: 0.45rem;
  font-size: 1rem;
  font-weight: 700;
}

.metric-label {
  color: rgba(248, 255, 248, 0.78);
  line-height: 1.6;
}

.form-panel {
  display: flex;
  min-height: 100%;
  align-items: center;
  justify-content: center;
  padding: 2.4rem 2rem;
}

.form-panel__inner {
  width: 100%;
  max-width: 34rem;
}

.mobile-brand {
  margin-bottom: 1.5rem;
}

.form-header {
  margin-bottom: 1.75rem;
}

.form-kicker {
  color: #2d8f45;
}

.form-header h2 {
  color: #143020;
  font-size: clamp(1.85rem, 2.4vw, 2.45rem);
}

.form-subtitle {
  color: #62736a;
}

.status-alert {
  margin-bottom: 1.15rem;
  padding: 0.95rem 1rem;
  border-radius: 16px;
  line-height: 1.6;
}

.status-alert--success {
  background: rgba(48, 171, 76, 0.12);
  color: #1f6b35;
  border: 1px solid rgba(48, 171, 76, 0.18);
}

.status-alert--danger {
  background: rgba(194, 79, 79, 0.1);
  color: #a44747;
  border: 1px solid rgba(194, 79, 79, 0.14);
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.login-form ::v-deep .form-group {
  margin-bottom: 0.85rem;
}

.login-form ::v-deep label {
  margin-bottom: 0.4rem;
  font-weight: 700;
  color: #173221;
}

.login-form ::v-deep small,
.login-form ::v-deep .description {
  color: #6a7b72;
}

.login-form ::v-deep input {
  min-height: 3.35rem;
  border: 1px solid rgba(21, 58, 31, 0.12);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.65);
}

.login-form ::v-deep input:focus {
  border-color: rgba(48, 171, 76, 0.55);
  box-shadow: 0 0 0 4px rgba(48, 171, 76, 0.12);
}

.login-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 3.45rem;
  margin-top: 0.4rem;
  border: 0;
  border-radius: 18px;
  background: linear-gradient(135deg, #16391f 0%, #30ab4c 100%);
  box-shadow: 0 16px 30px rgba(48, 171, 76, 0.18);
  color: #fff;
  font-weight: 700;
  letter-spacing: 0.01em;
  transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
}

.login-button:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 20px 36px rgba(48, 171, 76, 0.22);
}

.login-button:disabled {
  opacity: 0.72;
  cursor: not-allowed;
}

.auth-switch {
  margin-top: 1rem;
  text-align: center;
  color: #64766c;
}

.auth-switch a {
  color: #22753a;
  font-weight: 700;
}

@media (max-width: 991px) {
  .login-page {
    padding: 1rem 0;
  }

  .login-shell {
    min-height: calc(100vh - 2rem);
  }

  .form-panel {
    padding: 2rem 1.25rem;
  }
}

@media (max-width: 575px) {
  .login-frame {
    border-radius: 22px;
  }

  .form-panel {
    padding: 1.5rem 1rem;
  }

  .form-header h2,
  .mobile-brand h1 {
    font-size: 1.85rem;
  }
}
</style>
